/**
 * Wishlist → Horizon cart sync
 *
 * Refreshes Horizon's cart drawer, cart page and cart count bubble
 * after the wishlist app's ".ydp-add-to-cart-btn" adds a product to the cart.
 *
 * Install: load this in <head> of layout/theme.liquid, ABOVE wishlist.js,
 * without "defer" or "async":
 *   <script src="{{ 'wishlist-cart-sync.js' | asset_url }}"></script>
 */
(() => {
  // ---------- Settings ----------
  const BUTTON_SELECTOR = '.ydp-add-to-cart-btn';
  const OPEN_CART_DRAWER = true; // set to false if the drawer should stay closed
  const CLICK_WINDOW_MS = 10000; // how long after a click we watch for the cart request
  const DEBUG = false;           // set to true to see what's happening in the console
  // ------------------------------

  if (window.__ydpCartSync) return;
  window.__ydpCartSync = true;

  const nativeFetch = window.fetch.bind(window);
  let lastClickAt = 0;
  let refreshTimer = null;

  const log = (...args) => DEBUG && console.log('[wishlist-cart-sync]', ...args);
  const root = () => window.Shopify?.routes?.root || '/';
  const isCartAdd = (url) => typeof url === 'string' && url.includes('/cart/add');
  const clickedRecently = () => Date.now() - lastClickAt < CLICK_WINDOW_MS;

  // 1. Remember when a wishlist "Add to cart" button is clicked
  //    (capture phase, so this runs before the app's own click handler)
  document.addEventListener(
    'click',
    (event) => {
      const path = event.composedPath ? event.composedPath() : [event.target];
      const isWishlistButton = path.some(
        (el) => el instanceof Element && el.matches(BUTTON_SELECTOR)
      );
      if (isWishlistButton) {
        lastClickAt = Date.now();
        log('Wishlist add-to-cart clicked');
      }
    },
    true
  );

  // 2. Watch fetch() requests to /cart/add
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    try {
      const input = args[0];
      const url = input instanceof Request ? input.url : String(input);
      if (isCartAdd(url) && clickedRecently() && response.ok) {
        log('Cart add detected (fetch):', url);
        scheduleRefresh();
      }
    } catch (e) {
      /* never break the original request */
    }
    return response;
  };

  // 3. Watch XMLHttpRequest / jQuery $.ajax requests to /cart/add
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isCartAdd(String(url))) {
      this.addEventListener('load', () => {
        if (clickedRecently() && this.status >= 200 && this.status < 300) {
          log('Cart add detected (XHR):', url);
          scheduleRefresh();
        }
      });
    }
    return originalOpen.call(this, method, url, ...rest);
  };

  // Collapse several quick adds into one refresh
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 150);
  }

  // 4. Tell Horizon the cart changed, the same way its own product form does
  async function refreshCart() {
    try {
      const cart = await nativeFetch(`${root()}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      // Fresh HTML for the cart drawer / cart page sections on this page
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      let sections = {};
      if (sectionIds.length) {
        sections = await nativeFetch(`${root()}cart?sections=${sectionIds.join(',')}`)
          .then((r) => r.json())
          .catch(() => ({}));
      }

      const data = { source: 'ydp-wishlist', itemCount: cart.item_count, sections };

      let cartEvent;
      try {
        // Use Horizon's own event class when available
        const { CartAddEvent } = await import('@theme/events');
        cartEvent = new CartAddEvent(cart, 'ydp-wishlist', data);
      } catch (e) {
        // Fallback: same event name and shape
        cartEvent = new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'ydp-wishlist', data },
        });
      }

      document.dispatchEvent(cartEvent);
      log('Cart refreshed. Item count:', cart.item_count);

      if (OPEN_CART_DRAWER) setTimeout(openCartDrawer, 100);
    } catch (error) {
      console.warn('[wishlist-cart-sync] Could not refresh cart:', error);
    }
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || drawer.querySelector('dialog[open]')) return; // no drawer, or already open
    try {
      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    } catch (e) {
      log('Could not open cart drawer:', e);
    }
  }
})();